import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { pathTightBounds } from '@/lib/elements/pen-path-utils'
import type { NodeEditOverlayShapeGeom } from '../types/node-edit-overlay-props'
import { shiftPointsBack } from './anchor-points'

/**
 * Expand the shape bounding box to fit all anchor points.
 * Only ever grows the box (never shrinks during live drag).
 * Returns null when no change is needed, or the normalized points + new geom.
 */
export function expandToFitPoints(
  pts: PenAnchorPoint[],
  geom: NodeEditOverlayShapeGeom,
  isClosed: boolean,
  subpaths: PenSubpathRange[]
): { pts: PenAnchorPoint[]; geom: NodeEditOverlayShapeGeom } | null {
  if (pts.length === 0) return null

  const tight = pathTightBounds(pts, isClosed, subpaths)

  if (!tight) return null

  const { minX, minY, maxX, maxY } = tight

  // ox/oy are <= 0 (left/top expansion shift)
  const ox = Math.min(0, minX)
  const oy = Math.min(0, minY)
  const newW = Math.max(geom.width, maxX) - ox
  const newH = Math.max(geom.height, maxY) - oy

  if (ox === 0 && oy === 0 && newW === geom.width && newH === geom.height) return null

  const normalizedPts = shiftPointsBack(pts, ox, oy)

  return {
    pts: normalizedPts,
    geom: { x: geom.x + ox, y: geom.y + oy, width: newW, height: newH },
  }
}
